import queryString from 'query-string'

import type {
  IProfile,
  IProfileDto,
  IUpdateUserAvatar,
  TProfileUpdate
} from '@web/entities/user'
import { toProfile } from '@web/entities/user/api/mappers'
import { ApiResource, ApiTag } from '@web/shared/constants/api.constants'
import { baseApi } from '@web/shared/infrastructure/api-base-query'
import type { IPagination } from '@web/shared/types/paged-response.type'
import type { IPaginationQuery } from '@web/shared/types/pagination-query.type'

export const profileApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProfile: builder.query<IProfile, void>({
      query: () => ({
        url: ApiResource.Profile
      }),
      transformResponse: (response: IProfileDto) => toProfile(response),
      providesTags: [ApiTag.Profile]
    }),

    getProfiles: builder.query<
      IPagination<IProfile>,
      { query?: IPaginationQuery }
    >({
      query: (args) => {
        const queryObj = args?.query
        const query = queryString.stringify({ ...queryObj })
        return {
          url: `${ApiResource.Profile}/get-all?${query}`
        }
      },
      transformResponse: (
        response: IPagination<IProfileDto>
      ): IPagination<IProfile> => ({
        ...response,
        content: response.content.map(toProfile)
      }),
      providesTags: [ApiTag.Profile]
    }),

    updateProfile: builder.mutation<null, { body: TProfileUpdate }>({
      query: ({ body }) => ({
        url: `${ApiResource.Profile}`,
        method: 'PUT',
        body: body
      }),
      invalidatesTags: [ApiTag.Profile]
    }),

    updateAvatar: builder.mutation<null, IUpdateUserAvatar & { file: File }>({
      query: ({ file, cropHeight, cropX, cropY, cropWidth }) => {
        const stringifyParams = queryString.stringify({
          cropX,
          cropY,
          cropWidth,
          cropHeight
        })
        return {
          url: `${ApiResource.Profile}/upload-avatar?${stringifyParams}`,
          method: 'POST',
          body: file
        }
      },
      invalidatesTags: [ApiTag.Images, ApiTag.Profile]
    }),

    deleteAvatar: builder.mutation<void, void>({
      query: () => ({
        url: `${ApiResource.Profile}/delete-avatar`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTag.Profile]
    })
  })
})

export const {
  useGetProfileQuery,
  useGetProfilesQuery,
  useDeleteAvatarMutation,
  useLazyGetProfileQuery,
  useUpdateProfileMutation
} = profileApi
